import { useTranslation } from 'react-i18next'
import { PageTitle } from '../components/common/PageTitle/PageTitle'
import { RoleRestrictedContent } from '@/components/molecules/RoleRestrictedContent/RoleRestrictedContent'
import { UserType } from '@/hooks'
import { ActiveManagementTabs } from '@/features/ActiveManagement/components/ActiveManagementTabs/ActiveManagementTabs'
import { Outlet, useLocation } from '@tanstack/react-router'
import { ActiveManagementHeaderSlotProvider } from '@/features/ActiveManagement/components/ActiveManagementHeaderSlot/ActiveManagementHeaderSlotProvider'
import { useActiveManagementHeaderSlot } from '@/features/ActiveManagement/components/ActiveManagementHeaderSlot/useActiveManagementHeaderSlot'
import { ReportHeader } from '@/features/Reporting/components/ReportHeader/ReportHeader'
import { ActiveManagementFilters } from '@/features/ActiveManagement/components/ActiveManagementFilters/ActiveManagementFilters'
import useActiveManagementStore from '@/features/ActiveManagement/stores/useActiveManagementStore/useActiveManagementStore'
import { useNodesFiltersError } from '@/hooks/useNodesFiltersError/useNodesFiltersError'
import { FilterError } from '@/components/common/FilterError/FilterError'
import { useMemo } from 'react'
import { ActiveManagementFiltersValues } from '@/utils/constants'

export const ActiveManagement = () => {
    const { t } = useTranslation('translations')
    const { slot } = useActiveManagementHeaderSlot()
    const { pathname } = useLocation()
    const title = t('sidebar_menu.active_management')
    const isIntervalTab = pathname === '/active-management/interval'
    const { isFiltersError } = useNodesFiltersError()
    const reportSettings = useActiveManagementStore(
        (state) => state.intervalData.reportSettings
    )

    const selectedFilter = useMemo<
        keyof ActiveManagementFiltersValues | undefined
    >(() => {
        if (!isIntervalTab) return undefined
        return reportSettings.display === 'meters' ? 'meter' : 'subMeter'
    }, [isIntervalTab, reportSettings.display])

    return (
        <div className="grid h-full grow grid-cols-[100%] grid-rows-[auto_1fr]">
            <div className="space-y-6">
                <ReportHeader
                    title={title}
                    renderTitle={() => (
                        <div className="flex items-center">
                            <PageTitle
                                label={title}
                                labelTestId="page-title-active-management"
                            />
                        </div>
                    )}
                >
                    {slot}
                </ReportHeader>
                <RoleRestrictedContent
                    allowedRoles={[UserType.CUSTOMER]}
                    strict={false}
                >
                    <ActiveManagementTabs />
                    {isIntervalTab && (
                        <>
                            {isFiltersError ? (
                                <FilterError />
                            ) : (
                                <ActiveManagementFilters
                                    selectedFilter={selectedFilter}
                                />
                            )}
                        </>
                    )}
                </RoleRestrictedContent>
            </div>
            {(!isFiltersError || !isIntervalTab) && <Outlet />}
        </div>
    )
}

const ActiveManagementWithContext = () => {
    return (
        <ActiveManagementHeaderSlotProvider>
            <ActiveManagement />
        </ActiveManagementHeaderSlotProvider>
    )
}

export default ActiveManagementWithContext
